import React, { useEffect, useMemo, useState } from "react";
import {
  CircularProgress,
  LinearProgress,
  MenuItem,
  TextField,
} from "@material-ui/core";
import styled from "styled-components";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { FIELD_TYPES, FIELD_TYPE_OPTIONS } from "./constants";
import API from "../../../app/utils/api";
import Content from "./country.tabs/content";
import Controls from "../schedule.common/controls";
import CountryTabs from "./country.tabs";
import ExcelExports from "./excel.exports";
import ImportCost from "./import.country.costs";
import UploadCost from "./excel.uploads/upload.country.costs";
import UploadContract from "./excel.uploads/upload.site.contract";
import { Tooltip } from "../../../app/components";
import { useScheduleEvents } from "../../../app/contexts/schedule.events.context";
import { useSnackbar } from "../../../app/contexts/snackbar.context";
import { useUser } from "../../../app/contexts/user.context";
import OutlinedButton from "../../../app/components/buttons/outlined";
import CustomDatePicker from "../../../app/components/custom.date.picker";
import CurrencyDropdown from "../../../app/components/custom.dropdown/currency.dropdown";
import CustomDropdown from "../../../app/components/custom.dropdown";
import {
  useCountryTabChange,
  useSiteTabChange,
} from "./hooks/useCountrySiteTabChange";
import UnsavedChangesModal from "./unsaved.changes.modal";

export default function SOE() {
  const {
    fetchSOEVisits,
    fetchSOEAssessments,
    fetchASingleSOE,
    fetchSOEMapping,
    fetchSOECountries,
    fetchRiskAssessments,
    fetchVisitSampleStrategy,
    SOECountries,
    loadingCountrySOE,
    countryCurrencyData,
    siteCurrencyData,
    handleCurrencyChange,
    handleSiteCurrencyChange,
    siteContractEffectiveDate,
    setSiteContractEffectiveDate,
    setSiteContractEffectiveDateChangeLog,
    scheduleOfEventsVisits,
    updateSiteContractDateForTab,
    visitsHasCost,
  } = useScheduleEvents();

  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const [fieldType, setFieldType] = useState(
    queryParams.get("bf") || FIELD_TYPES["VISIT"],
  );
  const [siteId, setSiteId] = useState(() => {
    const initialSiteId = parseInt(queryParams.get("siteId") || "1");
    return initialSiteId;
  });
  const [siteNumber, setSiteNumber] = useState(null);

  const [working, setWorking] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const { loadingSOEStudy } = useScheduleEvents();
  const [value, setValue] = useState(() => {
    // Calculate initial tab value from URL instead of defaulting to 0
    const tabValue = parseInt(queryParams.get("sg")) || 0;
    return tabValue;
  });
  
  // Flag to track if initial setup was completed to avoid redundant fallback
  const [initialSetupCompleted, setInitialSetupCompleted] = useState(false);
  const [renderKey] = useState("");
  const { showSnackbar } = useSnackbar();
  const [hasChanges, setHasChanges] = useState(false);
  const [updateContext, setUpdateContext] = useState(false);
  const [saveSOE, setSaveSOE] = useState(false);
  const { id, scheduleId } = useParams();
  const { isReadOnlyUser, setURLStudyId } = useUser();

  const countryValues = useMemo(() => {
    return SOECountries.filter(
      (country) => country.siteId === 1 || country.siteGroupId === 0,
    );
  }, [SOECountries]);

  // Calculate if contract date is required based on current site having costs
  const isContractDateRequired = useMemo(() => {
    if (!countryValues[value] || siteId === 1) {
      return false; // Not required for country level (siteId === 1)
    }
    const key = `${countryValues[value]?.siteGroupId}-${siteId}`;
    return visitsHasCost[key]?.hasCost || false;
  }, [countryValues, value, siteId, visitsHasCost]);

  const [scrolled, setScrolled] = useState(false);

  const handleScroll = () => {
    setScrolled(window.scrollY > 0);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setURLStudyId(id);
    (async () => {
      // Execute all API calls in parallel for better performance
      const [
        soeResponse,
        ,  // visits
        ,  // assessments
        ,  // mapping
        countriesResponse,  // countries
        ,  // riskAssessments
        ,  // visitSampleStrategy
      ] = await Promise.all([
        fetchASingleSOE(scheduleId),
        fetchSOEVisits(scheduleId, id),
        fetchSOEAssessments(scheduleId, id),
        fetchSOEMapping(scheduleId),
        API.getSOECountries(scheduleId),  // Direct API call to avoid extra loading states
        fetchRiskAssessments(scheduleId),
        fetchVisitSampleStrategy(scheduleId),
      ]);
      
      // Process countries data manually to avoid extra renders
      if (countriesResponse?.data) {
        const currency = soeResponse?.data?.[0]?.currency;
        const studyCountry = {
          countryCodeISO3: "Study",
          siteGroupId: 0,
          ...(currency && { currency })
        };
        
        const allCountries = [studyCountry, ...countriesResponse.data];
        
        // Update SOECountries state only once with all data
        fetchSOECountries(scheduleId, soeResponse?.data?.[0], allCountries);
        
        // Handle tab selection and siteContractDate in the same render cycle
        const tabValue = parseInt(queryParams.get("sg")) || 0;
        const initialSiteId = parseInt(queryParams.get("siteId")) || 1;
        
        // Only update tab value if it's different from current value (avoid unnecessary renders)
        if (tabValue >= 0 && tabValue < allCountries.length && tabValue !== value) {
          setValue(tabValue);
        }
        
        // Update siteContractEffectiveDate using data already loaded (regardless of setValue)
        if (tabValue >= 0 && tabValue < allCountries.length) {
          const processedCountryValues = allCountries.filter(
            (country) => country.siteId === 1 || country.siteGroupId === 0,
          );
          const country = processedCountryValues[tabValue];
          if (country) {
            // Use the same logic as updateSiteContractDateForTab but synchronously
            const key = `${country.siteGroupId}-${initialSiteId}`;
            const visits = scheduleOfEventsVisits[key];
            if (visits?.length > 0) {
              const input = {
                siteId: visits?.[0]?.siteId || initialSiteId,
                siteContractEffectiveDate: visits?.[0]?.siteContractEffectiveDate,
                siteGroupId: visits?.[0]?.siteGroupId || country.siteGroupId,
                scheduleOfEventsId: visits?.[0]?.scheduleOfEventsId,
              };
              setSiteContractEffectiveDate(prev =>
                prev[key]
                  ? { ...prev, current: key }
                  : { ...prev, [key]: input, current: key }
              );
              setSiteContractEffectiveDateChangeLog(prev =>
                prev[key]
                  ? { ...prev, current: key }
                  : { ...prev, [key]: input, current: key }
              );
            }
          }
        } else {
          setValue(0);
          // Update for first tab if available using same logic
          const processedCountryValues = allCountries.filter(
            (country) => country.siteId === 1 || country.siteGroupId === 0,
          );
          if (processedCountryValues.length > 0) {
            const key = `${processedCountryValues[0].siteGroupId}-${initialSiteId}`;
            const visits = scheduleOfEventsVisits[key];
            if (visits?.length > 0) {
              const input = {
                siteId: visits?.[0]?.siteId || initialSiteId,
                siteContractEffectiveDate: visits?.[0]?.siteContractEffectiveDate,
                siteGroupId: visits?.[0]?.siteGroupId || processedCountryValues[0].siteGroupId,
                scheduleOfEventsId: visits?.[0]?.scheduleOfEventsId,
              };
              setSiteContractEffectiveDate(prev =>
                prev[key]
                  ? { ...prev, current: key }
                  : { ...prev, [key]: input, current: key }
              );
              setSiteContractEffectiveDateChangeLog(prev =>
                prev[key]
                  ? { ...prev, current: key }
                  : { ...prev, [key]: input, current: key }
              );
            }
          }
        }
        
        // Mark initial setup as completed AFTER all processing is done
        setInitialSetupCompleted(true);
      }
    })();
    // eslint-disable-next-line
  }, []);

  const updateUrlWithNewQueryParams = (updates) => {
    if (!Object.keys(updates)) return;
    const existingParams = {};
    for (let [key, value] of queryParams.entries()) {
      existingParams[key] = value;
    }
    const totalParams = { ...existingParams, ...updates };
    navigate({
      pathname: location.pathname,
      search: `?${Object.keys(totalParams)
        .map((key) => `${key}=${totalParams[key]}`)
        .join("&")}`,
    });
  };

  const sites = useMemo(() => {
    return SOECountries.filter(
      (country) => country.siteGroupId === countryValues[value]?.siteGroupId,
    ).sort((site) => site.siteNumber);
  }, [SOECountries, countryValues, value]);

  const refreshSiteList = () => {
    setWorking(true);
    API.refreshSiteSelectedList(id, scheduleId)
      .then(async () => {
        setWorking(false);
        // Refresh all SOE data in parallel including countries
        const [
          soeResponse,
          ,  // visits
          ,  // assessments
          ,  // mapping
          countriesResponse,  // countries
          ,  // riskAssessments
          ,  // visitSampleStrategy
        ] = await Promise.all([
          fetchASingleSOE(scheduleId),
          fetchSOEVisits(scheduleId, id),
          fetchSOEAssessments(scheduleId, id),
          fetchSOEMapping(scheduleId),
          API.getSOECountries(scheduleId),  // Direct API call
          fetchRiskAssessments(scheduleId),
          fetchVisitSampleStrategy(scheduleId),
        ]);
        
        // Process countries data manually to avoid extra loading states
        if (countriesResponse?.data) {
          const currency = soeResponse?.data?.[0]?.currency;
          const studyCountry = {
            countryCodeISO3: "Study",
            siteGroupId: 0,
            ...(currency && { currency })
          };
          
          // Update SOECountries state only once with all data
          fetchSOECountries(scheduleId, soeResponse?.data?.[0], [studyCountry, ...countriesResponse.data]);
        }
        
        showSnackbar("Site list refreshed", "success");
      })
      .catch(() => {
        showSnackbar("Failed to refresh site list", "error");
        setWorking(false);
      });
  };

  // Update siteContractEffectiveDate when siteId changes (simplified - only for user interactions)
  useEffect(() => {
    // Only execute for user interactions after initial setup is complete
    if (!initialSetupCompleted) {
      return;
    }
    
    if (countryValues[value] && siteId && SOECountries.length > 0) {
      // Check if siteContractEffectiveDate is already set for this combination
      const currentSiteGroupId = countryValues[value].siteGroupId;
      const isAlreadyConfigured = !!siteContractEffectiveDate?.[`${currentSiteGroupId}-${siteId}`];
      
      if (isAlreadyConfigured) {
        return;
      }
      
      updateSiteContractDateForTab(countryValues[value].siteGroupId, siteId);
    }
    // eslint-disable-next-line
  }, [siteId, value, initialSetupCompleted, siteContractEffectiveDate]);

  // Updated currency dropdown change handler to support both country and site level
  const handleCurrencyDropdownChange = (event, selectedCurrency) => {
    if (siteId === 1) {
      // Country level
      handleCurrencyChange(countryValues[value]?.siteGroupId, selectedCurrency?.currencyCode || "");
    } else {
      // Site level
      handleSiteCurrencyChange(
        countryValues[value]?.siteGroupId, 
        siteId, 
        selectedCurrency?.currencyCode || ""
      );
    }
  };

  // Memoized currency value to avoid stale data when switching tabs
  const currencyValue = useMemo(() => {
    const currentCountry = countryValues[value];
    if (!currentCountry || currentCountry.siteGroupId == null) {
      return "";
    }
    if (siteId === 1) {
      return countryCurrencyData[currentCountry.siteGroupId]?.currency ?? "";
    }
    const siteKey = `${currentCountry.siteGroupId}-${siteId}`;
    return siteCurrencyData[siteKey]?.currency ?? "";
  }, [countryValues, value, siteId, countryCurrencyData, siteCurrencyData]);

  // Shared modal state for unsaved changes
  const [modalOpen, setModalOpen] = useState(false);
  const [modalButtons, setModalButtons] = useState([]);
  const [modalConfig, setModalConfig] = useState({
    title: "You Have Unsaved Changes",
    message: "",
  });

  // Use hook to handle country tab changes with unsaved changes validation
  const handleCountryTabChange = useCountryTabChange({
    countryValues,
    value,
    studyId: id,
    scheduleId,
    updateUrlWithNewQueryParams,
    setValue,
    setSiteId,
    setModalOpen,
    setModalButtons,
    setModalConfig,
  });

  // Use hook to handle site changes with unsaved changes validation
  const handleSiteChange = useSiteTabChange({
    siteId,
    sites,
    setSiteId,
    setSiteNumber,
    queryParams,
    navigate,
    countryValues,
    value,
  });

  return (
    <Container>
      {loadingSOEStudy ? (
        <LinearProgress />
      ) : (
        <>
          <HeaderContainer className={scrolled ? "scrolled" : ""}>
            <Controls
              editOpen={editOpen}
              setEditOpen={setEditOpen}
              hasChangesToSave={hasChanges}
              setUpdateContext={(value) => {
                setHasChanges(value);
                setUpdateContext(value);
              }}
              saveSOE={saveSOE}
              value={value}
            />
          </HeaderContainer>
          {countryValues.length > 0 && (
            <CountryTabs
              tabs={countryValues}
              value={value >= 0 && value < countryValues.length ? value : 0}
              onChange={handleCountryTabChange}
            />
          )}
          <ContentContainer>
            <TopLevelContainer>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "1em",
                  position: "sticky",
                }}
              >
                <div style={{ display: "flex", gap: "1em" }}>
                  {/* Currency dropdown - show for both country and site level */}
                  <CurrencyDropdown
                    key={`currency-${countryValues[value]?.siteGroupId}-${siteId}`}
                    value={currencyValue}
                    onChange={handleCurrencyDropdownChange}
                    label={"Currency"}
                    size="small"
                    variant="outlined"
                    disabled={isReadOnlyUser}
                    readOnly={isReadOnlyUser}
                  />
                </div>

                <div style={{ display: "flex", gap: "1em" }}>
                  {countryValues[value]?.siteId !== 1 && (
                    <>
                      <TextField
                        style={{ width: "220px", height: "40px" }}
                        select
                        label="Business Function"
                        id="field-type"
                        value={fieldType}
                        onChange={(event) => {
                          setFieldType(event.target.value);
                          updateUrlWithNewQueryParams({ bf: event.target.value });
                        }}
                        size="small"
                        variant="outlined"
                        InputProps={{
                          style: {
                            fontSize: "1rem",
                            height: "40px",
                          },
                        }}
                        InputLabelProps={{
                          style: {
                            fontSize: "1rem",
                          },
                        }}
                      >
                        {FIELD_TYPE_OPTIONS.map((fto) => (
                          <MenuItem
                            key={fto.value}
                            value={fto.value}
                          >
                            {fto.label}
                          </MenuItem>
                        ))}
                      </TextField>
                      <ExcelExports/>
                    </>
                  )}
                  {countryValues[value]?.siteId === 1 && (
                    <>
                      {siteId !== 1 && (
                        <DatePickerContainer>
                          <CustomDatePicker
                            label="Contract Effective Date"
                            value={(() => {
                              const key = `${countryValues[value]?.siteGroupId}-${siteId}`;
                              const dateStr = siteContractEffectiveDate?.[key]?.siteContractEffectiveDate;
                              return dateStr ? new Date(dateStr) : null;
                            })()}
                            onChange={(dateValue) => {
                              const formattedDate = dateValue ? new Date(dateValue).toISOString().split('T')[0] : null;
                              const key = `${countryValues[value]?.siteGroupId}-${siteId}`;
                              const input = {
                                siteId: siteId,
                                siteContractEffectiveDate: formattedDate,
                                siteGroupId: countryValues[value]?.siteGroupId,
                                scheduleOfEventsId: scheduleId,
                              };
                              setSiteContractEffectiveDate(prev => ({ ...prev, [key]: input, current: key }));
                            }}
                            showYearDropdown
                            showMonthDropdown
                            useShortMonthInDropdown
                            dateFormat={"dd/MMM/yyyy"}
                            size="small"
                            isClearable={true}
                            readOnly={isReadOnlyUser}
                            disabled={isReadOnlyUser}
                            required={isContractDateRequired}
                            error={(() => {
                              const key = `${countryValues[value]?.siteGroupId}-${siteId}`;
                              return isContractDateRequired && !siteContractEffectiveDate?.[key]?.siteContractEffectiveDate;
                            })()}
                          />
                        </DatePickerContainer>
                      )}
                      <CustomDropdown
                        label="Site List"
                        value={siteId}
                        onChange={handleSiteChange}
                        items={sites}
                        getItemLabel={(site) => site.dropDownListLabel}
                        getItemValue={(site) => site.siteId}
                        filterItems={(sites, searchText) => {
                          if (!searchText) return sites;
                          return sites.filter((site) =>
                            site.dropDownListLabel.toLowerCase().includes(searchText.toLowerCase())
                          );
                        }}
                        showClearOption={false}
                      />
                      {!isReadOnlyUser && (
                        siteId === 1 ? (
                          <>
                            <UploadCost
                              studyId={id}
                              scheduleId={scheduleId}
                            />
                            <ImportCost
                              studyId={id}
                              scheduleId={scheduleId}
                              siteGroupId={countryValues[value]?.siteGroupId}
                            />
                          </>
                        ) : (
                          <UploadContract
                            studyId={id}
                            scheduleId={scheduleId}
                            siteGroupId={countryValues[value]?.siteGroupId}
                            siteId={siteId}
                            siteNumber={siteNumber}
                          />
                        )
                      )}
                      {!isReadOnlyUser && (
                        <OutlinedButton
                          disabled={working}
                          style={{ width: "220px", height: "40px" }}
                          variant="outlined"
                          onClick={refreshSiteList}
                        >
                          {!working ? (
                            <div style={{ display: "flex", alignItems: "center" }}>
                              <span>REFRESH SITE LIST</span>{" "}
                              <Tooltip info="The list of sites will be refreshed for all countries. Any site that has a Fully Executed contract listed in the Remarque Activity module will become available to select so data entry for that Site can begin." />
                            </div>
                          ) : (
                            <CircularProgress size={"1.5rem"} />
                          )}
                        </OutlinedButton>
                      )}
                      <ExcelExports />
                    </>
                  )}
                </div>
              </div>
            </TopLevelContainer>

            {loadingCountrySOE && <LinearProgress />}
            <Content
              siteId={siteId}
              setSiteId={setSiteId}
              editOpen={editOpen}
              setEditOpen={setEditOpen}
              key={renderKey}
              tabs={countryValues}
              value={value}
              setValue={setValue}
              fieldType={fieldType}
              setFieldType={setFieldType}
              setHasChanges={setHasChanges}
              updateContext={updateContext}
              setSaveSOE={setSaveSOE}
              setUpdateContext={setUpdateContext}
            />
          </ContentContainer>
          
          {/* Unified modal for unsaved changes (country/site tabs) */}
          <UnsavedChangesModal
            open={modalOpen}
            onToggle={() => setModalOpen(!modalOpen)}
            title={modalConfig.title}
            message={modalConfig.message}
            buttons={modalButtons}
          />
        </>
      )}
    </Container>
  );
}

const Container = styled.div`
  display: grid;
  grid-gap: 1em;
`;

const ContentContainer = styled.div`
  flex: 1;
`;

const HeaderContainer = styled.div`
  position: sticky;
  top: 10.3em;
  left: 0;
  background: var(--White);
  z-index: 1100;
  width: 100%;
  display: flex;
  flex-direction: column;
  padding-block: 1em;
`;

const TopLevelContainer = styled.div`
  position: relative;
  overflow: visible;
  z-index: 1000;
`;

const DatePickerContainer = styled.div`
  /* Specific container for DatePicker with high z-index */
  position: relative;
  z-index: 1; /* Relative to TopLevelContainer which already has z-index: 10000 */
  background: transparent;
  display: inline-block;
  height: 40px; /* Match other elements height */
  
  /* Positioning to appear above everything */
  .react-datepicker-wrapper {
    position: relative;
    z-index: 1;
    height: 40px; /* Ensure wrapper matches */
  }
  
  .react-datepicker__input-container input {
    height: 40px !important;
    box-sizing: border-box;
    padding: 8px 12px;
    font-size: 1rem;
  }
  
  .react-datepicker-popper {
    z-index: 999 !important;
  }
  
  .react-datepicker {
    z-index: 999 !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;